import React, { useCallback } from 'react';
import { Button } from 'antd';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';

import { CertificateStatusEnum, ItemTypeEnum } from '@share/common-types';
import { Routes } from '@share/constants';
import { walletDrawerActions } from '@share/store/slices';

import type { WalletItemProps } from '../../types';
import {
  DemoAccountName,
  DemoDomainName,
  CERTIFICATE_DEMO_CERTIFICATE_CODE_QUERY,
  CERTIFICATE_DEMO_COVERAGE_AMOUNT_QUERY,
  CERTIFICATE_DEMO_NIGHTS_QUERY,
} from '../../constants';
import {
  getWalletItemActionText,
  getWalletItemIcon,
  getWalletItemState,
  getWalletItemStatusText,
  isWalletItemActionDisabled,
} from '../../utils';

import styles from './styles.module.scss';

const itemTypeRouteMap: Record<ItemTypeEnum, Routes> = {
  [ItemTypeEnum.Hotel]: Routes.Search,
  [ItemTypeEnum.Condo]: Routes.CondoSearch,
  [ItemTypeEnum.Cruise]: Routes.Cruises,
};

export const WalletItem = ({
  item,
  accountUsername,
  certificateCode,
  certificateTotalPrice,
  certificateStatus,
  certificateExpiresAt,
  certificateUpdatedAt,
}: WalletItemProps): React.ReactElement => {
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();
  const displayParams = { item, certificateStatus, certificateExpiresAt };
  const statusText: string = getWalletItemStatusText(
    { ...displayParams, certificateUpdatedAt },
    intl,
  );
  const actionText: string = getWalletItemActionText(displayParams, intl);
  const isDisabled: boolean = isWalletItemActionDisabled(item, certificateStatus);
  const stateClassName: string = getWalletItemState(displayParams);

  const handleActionClick = useCallback((): void => {
    const route: Routes | undefined = itemTypeRouteMap[item.type];

    if (!route) {
      return;
    }

    if (certificateStatus === CertificateStatusEnum.Pending) {
      const origin: string = DemoDomainName.replace(/\/$/, '');
      const params = new URLSearchParams();
      params.set(CERTIFICATE_DEMO_NIGHTS_QUERY, String(item.nights));
      params.set(CERTIFICATE_DEMO_COVERAGE_AMOUNT_QUERY, String(certificateTotalPrice));
      params.set(CERTIFICATE_DEMO_CERTIFICATE_CODE_QUERY, certificateCode);
      const url = `${origin}/${DemoAccountName}${route}?${params.toString()}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      dispatch(walletDrawerActions.setWalletDrawerOpen(false));
      return;
    }

    history.push(`/${accountUsername}${route}`, {
      certificateId: item.certificateId,
      certificateItemId: item.id,
    });
    dispatch(walletDrawerActions.setWalletDrawerOpen(false));
  }, [
    accountUsername,
    certificateCode,
    certificateStatus,
    certificateTotalPrice,
    dispatch,
    history,
    item,
  ]);

  return (
    <div className={styles.walletItem}>
      <div className={styles.iconWrap}>
        <img src={getWalletItemIcon(item)} alt={item.type} />
      </div>

      <div className={styles.content}>
        <p className={styles.name}>
          {intl.formatMessage(
            { id: 'certificates.card.tag.nights' },
            { type: item.type, nights: item.nights },
          )}
        </p>
        <p className={`${styles.state} ${styles[stateClassName]}`}>{statusText}</p>
      </div>

      {isDisabled ? (
        <span className={styles.redeemedLabel}>{actionText.toUpperCase()}</span>
      ) : (
        <Button className={styles.actionButton} type="default" onClick={handleActionClick}>
          {actionText}
        </Button>
      )}
    </div>
  );
};
